import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, X } from "lucide-react";
import { useLang } from "@/i18n";
import { PRIVACY } from "@/privacy";

const SHARED = {
  name: "Rest'Ô Bréda SARL",
  form: "SARL",
  capital: "1 000 €",
  siren: "109 246 421",
  rcs: "Grenoble",
  vat: "FR40109246421",
  office: "1850 Route du Haut-Bréda, 38580 Le Haut-Bréda, France",
  email: "contact@restobreda.com",
  privacyEmail: "privacy@restobreda.com",
};

const LEGAL = {
  fr: {
    link: "Mentions légales",
    title: "Mentions légales",
    director: "Jean-Philippe Vacher, Gérant",
    rows: [
      ["Raison sociale", "name"],
      ["Forme juridique", "form"],
      ["Capital social", "capital"],
      ["SIREN", "siren"],
      ["RCS", "rcs"],
      ["TVA intracommunautaire", "vat"],
      ["Siège social", "office"],
      ["Direction de la publication", "director"],
      ["Contact", "email"],
    ],
    privacyLabel: "Données personnelles",
    privacyText: "Pour toute question relative à vos données personnelles, écrivez à",
  },
  en: {
    link: "Legal notice",
    title: "Legal notice",
    director: "Jean-Philippe Vacher, Managing Partner",
    rows: [
      ["Company name", "name"],
      ["Legal form", "form"],
      ["Share capital", "capital"],
      ["SIREN", "siren"],
      ["Trade registry (RCS)", "rcs"],
      ["EU VAT number", "vat"],
      ["Registered office", "office"],
      ["Publication director", "director"],
      ["Contact", "email"],
    ],
    privacyLabel: "Personal data",
    privacyText: "For any question about your personal data, write to",
  },
  es: {
    link: "Aviso legal",
    title: "Aviso legal",
    director: "Jean-Philippe Vacher, Gerente",
    rows: [
      ["Denominación social", "name"],
      ["Forma jurídica", "form"],
      ["Capital social", "capital"],
      ["SIREN", "siren"],
      ["Registro mercantil (RCS)", "rcs"],
      ["IVA intracomunitario", "vat"],
      ["Domicilio social", "office"],
      ["Director de publicación", "director"],
      ["Contacto", "email"],
    ],
    privacyLabel: "Datos personales",
    privacyText: "Para cualquier pregunta sobre sus datos personales, escriba a",
  },
  de: {
    link: "Impressum",
    title: "Impressum",
    director: "Jean-Philippe Vacher, Geschäftsführer",
    rows: [
      ["Firmenname", "name"],
      ["Rechtsform", "form"],
      ["Stammkapital", "capital"],
      ["SIREN", "siren"],
      ["Handelsregister (RCS)", "rcs"],
      ["USt-IdNr.", "vat"],
      ["Sitz", "office"],
      ["Verantwortlich für den Inhalt", "director"],
      ["Kontakt", "email"],
    ],
    privacyLabel: "Personenbezogene Daten",
    privacyText: "Bei Fragen zu Ihren personenbezogenen Daten schreiben Sie an",
  },
  it: {
    link: "Note legali",
    title: "Note legali",
    director: "Jean-Philippe Vacher, Amministratore",
    rows: [
      ["Ragione sociale", "name"],
      ["Forma giuridica", "form"],
      ["Capitale sociale", "capital"],
      ["SIREN", "siren"],
      ["Registro delle imprese (RCS)", "rcs"],
      ["Partita IVA UE", "vat"],
      ["Sede legale", "office"],
      ["Direttore della pubblicazione", "director"],
      ["Contatto", "email"],
    ],
    privacyLabel: "Dati personali",
    privacyText: "Per qualsiasi domanda sui tuoi dati personali, scrivi a",
  },
  nl: {
    link: "Juridische vermeldingen",
    title: "Juridische vermeldingen",
    director: "Jean-Philippe Vacher, Zaakvoerder",
    rows: [
      ["Bedrijfsnaam", "name"],
      ["Rechtsvorm", "form"],
      ["Maatschappelijk kapitaal", "capital"],
      ["SIREN", "siren"],
      ["Handelsregister (RCS)", "rcs"],
      ["BTW-nummer", "vat"],
      ["Statutaire zetel", "office"],
      ["Publicatieverantwoordelijke", "director"],
      ["Contact", "email"],
    ],
    privacyLabel: "Persoonsgegevens",
    privacyText: "Voor vragen over uw persoonsgegevens schrijft u naar",
  },
  ru: {
    link: "Правовая информация",
    title: "Правовая информация",
    director: "Жан-Филипп Ваше, директор",
    rows: [
      ["Название компании", "name"],
      ["Организационно-правовая форма", "form"],
      ["Уставный капитал", "capital"],
      ["SIREN", "siren"],
      ["Торговый реестр (RCS)", "rcs"],
      ["НДС (ЕС)", "vat"],
      ["Юридический адрес", "office"],
      ["Ответственный за публикацию", "director"],
      ["Контакты", "email"],
    ],
    privacyLabel: "Персональные данные",
    privacyText: "По любым вопросам о ваших персональных данных пишите на",
  },
  uk: {
    link: "Правова інформація",
    title: "Правова інформація",
    director: "Жан-Філіпп Ваше, директор",
    rows: [
      ["Назва компанії", "name"],
      ["Організаційно-правова форма", "form"],
      ["Статутний капітал", "capital"],
      ["SIREN", "siren"],
      ["Торговий реєстр (RCS)", "rcs"],
      ["ПДВ (ЄС)", "vat"],
      ["Юридична адреса", "office"],
      ["Відповідальний за публікацію", "director"],
      ["Контакти", "email"],
    ],
    privacyLabel: "Персональні дані",
    privacyText: "З будь-яких питань щодо ваших персональних даних пишіть на",
  },
};

export default function LegalNotice() {
  const { lang } = useLang();
  const [open, setOpen] = useState(false);
  const l = LEGAL[lang] || LEGAL.fr;
  const valueFor = (key) => (key === "director" ? l.director : SHARED[key]);

  return (
    <>
      <button
        type="button"
        data-testid="footer-legal-link"
        onClick={() => setOpen(true)}
        className="text-xs text-paper/60 underline decoration-paper/30 underline-offset-4 transition-colors duration-300 hover:text-wood-light"
      >
        {l.link}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-pine-deep/70 p-5 backdrop-blur-sm"
            onClick={() => setOpen(false)}
            role="dialog"
            aria-modal="true"
            aria-label={l.title}
          >
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.98 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              data-testid="legal-modal"
              className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-paper p-8 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-6">
                <h3 className="font-serif text-3xl font-light tracking-tight text-pine">{l.title}</h3>
                <button
                  type="button"
                  data-testid="legal-close"
                  onClick={() => setOpen(false)}
                  aria-label="Fermer"
                  className="rounded-full p-2 text-pine/60 transition-colors hover:bg-pine/5 hover:text-pine"
                >
                  <X size={20} />
                </button>
              </div>

              <dl className="mt-8 space-y-4">
                {l.rows.map(([label, key]) => (
                  <div key={key} className="flex flex-col gap-0.5 border-b border-pine/10 pb-3">
                    <dt className="text-[10px] font-semibold uppercase tracking-[0.18em] text-pine/50">
                      {label}
                    </dt>
                    <dd className="text-sm text-pine">
                      {key === "email" ? (
                        <a href={`mailto:${SHARED.email}`} className="transition-colors hover:text-wood">
                          {SHARED.email}
                        </a>
                      ) : (
                        valueFor(key)
                      )}
                    </dd>
                  </div>
                ))}
              </dl>

              <div className="mt-8 rounded-xl bg-cream p-5">
                <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-pine/50">
                  {l.privacyLabel}
                </p>
                <p className="mt-2 text-sm leading-relaxed text-pine/80">
                  {l.privacyText}{" "}
                  <a
                    href={`mailto:${SHARED.privacyEmail}`}
                    data-testid="legal-privacy-email"
                    className="font-semibold text-pine underline decoration-wood/50 underline-offset-4 transition-colors hover:text-wood"
                  >
                    {SHARED.privacyEmail}
                  </a>
                  .
                </p>
                <button
                  type="button"
                  data-testid="legal-privacy-link"
                  onClick={() => {
                    setOpen(false);
                    window.dispatchEvent(new CustomEvent("open-privacy-policy"));
                  }}
                  className="group mt-4 inline-flex items-center gap-2 text-sm font-semibold text-pine transition-colors duration-300 hover:text-wood"
                >
                  {(PRIVACY[lang] || PRIVACY.fr).link}
                  <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
